import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Urls } from '@/apiConnection/urls';

const CHECK_INTERVAL_MS = 20000;

/**
 * Tracks whether the backend itself is actually reachable, not just whether the browser's
 * network interface is up. `navigator.onLine`/the `online`/`offline` events only reflect the
 * latter - a wifi connection with no route to the API server (backend down, wrong URL, VPN
 * dropped) still reports `navigator.onLine === true`. This pings the dedicated `GET /health`
 * endpoint (unauthenticated, no `LogUserActivity` noise - see `erp-backend`'s `routes/api.php`):
 * a 200 means the backend answered; only a real network failure (`status === 0`, the same
 * signal ApiService/OfflineDataInterceptor key off of) counts as unreachable.
 */
@Injectable({
    providedIn: 'root'
})
export class ConnectivityService {

    backendOnline = signal(navigator.onLine);

    private checking = false;

    constructor(private http: HttpClient) {
        window.addEventListener('online', () => this.check());
        window.addEventListener('offline', () => this.backendOnline.set(false));
        this.check();
        setInterval(() => this.check(), CHECK_INTERVAL_MS);
    }

    async check(): Promise<void> {
        if (!navigator.onLine) {
            this.backendOnline.set(false);
            return;
        }
        if (this.checking) return;
        this.checking = true;
        try {
            await firstValueFrom(this.http.get(Urls.urls.health));
            this.backendOnline.set(true);
        } catch (err: any) {
            this.backendOnline.set(err?.status !== 0);
        } finally {
            this.checking = false;
        }
    }
}
